import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import apiClient from '../../services/api/apiClient';
import ReplyBox from './ReplyBox';
import { useToast } from '../../components/ui/Toast/Toast';

function PostDetailPanel({ post, accountId, onReplySent, onClose, className }) {
  const { toast, confirm } = useToast();
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [isQuoting, setIsQuoting] = useState(false);
  const [isReposting, setIsReposting] = useState(false);

  if (!post) {
    return null;
  }

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleQuote = async () => {
    if (!quoteText.trim() || !accountId) return;

    setIsQuoting(true);
    try {
      await apiClient.post('/threads/posts/quote', {
        accountId,
        text: quoteText,
        quotePostId: post.id
      });

      toast.success('Quote posted successfully!');
      setShowQuoteModal(false);
      setQuoteText('');
      if (onReplySent) onReplySent();
    } catch (error) {
      console.error('Failed to quote post:', error);
      toast.error(error.response?.data?.message || 'Failed to quote post. Please try again.');
    } finally {
      setIsQuoting(false);
    }
  };

  const handleRepost = async () => {
    if (!accountId) return;

    const confirmed = await confirm({
      title: 'Repost to Your Account',
      message: 'Are you sure you want to repost this to your account?',
      confirmText: 'Repost',
      cancelText: 'Cancel'
    });

    if (!confirmed) return;

    setIsReposting(true);
    try {
      await apiClient.post('/threads/posts/repost', {
        accountId,
        postId: post.id
      });

      toast.success('Reposted successfully!');
      if (onReplySent) onReplySent();
    } catch (error) {
      console.error('Failed to repost:', error);
      toast.error(error.response?.data?.message || 'Failed to repost. Please try again.');
    } finally {
      setIsReposting(false);
    }
  };

  const containerClassName = [
    'w-full h-full flex flex-col overflow-hidden bg-white',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClassName}>
      {/* Header */}
      <div className="p-5 flex items-center justify-between border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">
          Post Details
        </h2>
        <div className="flex items-center gap-2">
          <a
            href={post.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors hover:bg-gray-100"
            title="View on Threads"
          >
            <Icon icon="mdi:open-in-new" className="text-lg text-gray-700" />
          </a>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors hover:bg-gray-100"
            title="Close"
          >
            <Icon icon="mdi:close" className="text-lg text-gray-700" />
          </button>
        </div>
      </div>

      {/* Post Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Author Info */}
        <div className="p-5 pb-3">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-base font-semibold bg-blue-100 text-blue-600">
              {post.username?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="text-base font-semibold text-gray-900">
                @{post.username}
              </p>
              <p className="text-xs text-gray-500">
                {formatDate(post.timestamp)}
              </p>
            </div>
          </div>

          {/* Full Post Text */}
          <div className="text-sm leading-relaxed whitespace-pre-wrap mb-4 text-gray-700">
            {post.text || 'No content'}
          </div>

          {/* Interaction Buttons */}
          <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-200">
            <button
              onClick={() => setShowQuoteModal(true)}
              disabled={!accountId}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon icon="mdi:comment-quote-outline" width={18} />
              Quote
            </button>
            <button
              onClick={handleRepost}
              disabled={!accountId || isReposting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon icon={isReposting ? "line-md:loading-loop" : "mdi:repeat"} width={18} />
              {isReposting ? 'Reposting...' : 'Repost'}
            </button>
          </div>
        </div>

        {/* Reply Section */}
        <div className="px-5 pb-5">
          <h3 className="text-sm font-semibold mb-3 text-gray-900">
            Reply to this post
          </h3>
          <ReplyBox
            postId={post.id}
            accountId={accountId}
            postContent={post.text}
            replyUsername={post.username}
            onReplySent={onReplySent}
          />
        </div>
      </div>

      {/* Quote Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowQuoteModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg m-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Quote Post</h3>
              <button
                onClick={() => setShowQuoteModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Icon icon="mdi:close" className="text-xl text-gray-700" />
              </button>
            </div>

            {/* Original Post Preview */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
              <p className="text-xs font-semibold text-gray-600 mb-1">@{post.username}</p>
              <p className="text-sm text-gray-700 line-clamp-3">{post.text}</p>
            </div>

            {/* Quote Text Input */}
            <textarea
              value={quoteText}
              onChange={(e) => setQuoteText(e.target.value)}
              placeholder="Add your comment..."
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 resize-none"
              rows={4}
            />

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => setShowQuoteModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleQuote}
                disabled={!quoteText.trim() || isQuoting}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isQuoting && <Icon icon="line-md:loading-loop" width={16} />}
                {isQuoting ? 'Posting...' : 'Post Quote'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PostDetailPanel;
